import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import cx from "../ui/cx";
import {
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";
import { formatQty } from "../../static/materialLine";
import { stockTextClass } from "../../static/materialStock";
import { MaterialStockSizeProps } from "../../types/materialStock";

type CountKey = "purchased" | "sent" | "added" | "less";

// How the stock comes about: bars bought and added (in), sent with cutting orders and taken (out).
const counts: { key: CountKey; label: string; isIn: boolean }[] = [
  { key: "purchased", label: "ખરીદી", isIn: true },
  { key: "sent", label: "કટીંગ ઓર્ડર", isIn: false },
  { key: "added", label: "ઉમેરો", isIn: true },
  { key: "less", label: "બાદ", isIn: false },
];

const countClass = (value: number, isIn: boolean) =>
  value === 0 ? "text-gray-400" : isIn ? "text-green-700" : "text-orange-700";

const cardRowClass = "flex items-center justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

type Props = {
  sizes: MaterialStockSizeProps[];
};

// A material's stock, counting every record, in one row with the stock last. The materials module doesn't show sizes
// (a new material has the one 3660 size), so a material made before that with more sizes shows them added up.
const MaterialStockTotals = ({ sizes }: Props) => {
  const sum = (key: CountKey | "stock") =>
    sizes.reduce((total, size) => total + (Number(size[key]) || 0), 0);

  const stock = sum("stock");

  return (
    <>
      <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
        <Table>
          <TableHeader>
            <TableRow className={detailTableHeadRowClass}>
              {counts.map((count) => (
                <TableHead
                  key={count.key}
                  className={cx(tableHeadCellClass, "text-right")}
                >
                  {count.label}
                </TableHead>
              ))}
              <TableHead className={cx(tableHeadCellClass, "text-right")}>
                સ્ટોક
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            <TableRow>
              {counts.map((count) => (
                <TableCell
                  key={count.key}
                  className={cx(
                    "text-right font-medium",
                    countClass(sum(count.key), count.isIn)
                  )}
                >
                  {formatQty(sum(count.key))}
                </TableCell>
              ))}
              <TableCell
                className={cx(
                  "text-right text-lg font-bold",
                  stockTextClass(stock)
                )}
              >
                {formatQty(stock)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <div className={cx(mobileCardClass, "sm:hidden")}>
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <span className="text-sm text-gray-500">સ્ટોક</span>
          <span className={cx("text-lg font-bold", stockTextClass(stock))}>
            {formatQty(stock)}
          </span>
        </div>

        <div className="space-y-2 p-4 text-sm">
          {counts.map((count) => (
            <div key={count.key} className={cardRowClass}>
              <span className={cardLabelClass}>{count.label}</span>
              <span
                className={cx(
                  "font-medium",
                  countClass(sum(count.key), count.isIn)
                )}
              >
                {formatQty(sum(count.key))}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MaterialStockTotals;
